import type { LocationWeeklyHours } from '@unify/fhir-model/resources/location';
import { DAY_OF_WEEK_CODES, type DayOfWeekCode } from '@unify/fhir-model/resources/schedule';
import {
  DEFAULT_PROVIDER_AVAILABILITY_SLOT,
  getDefaultProviderWeeklyAvailability,
  PROVIDER_AVAILABILITY_UI_DAYS,
  type ProviderAvailabilityUiDay,
  type ProviderWeeklyAvailability,
} from '@/features/calendar/providerAvailabilityTypes';
import { dayWindowHasSelectableRange, parseTimeLabel12h } from '@/features/calendar/providerAvailabilityValidation';

const UI_TO_FHIR: Record<ProviderAvailabilityUiDay, DayOfWeekCode> = {
  Sun: 'sun',
  Mon: 'mon',
  Tue: 'tue',
  Wed: 'wed',
  Thu: 'thu',
  Fri: 'fri',
  Sat: 'sat',
};

const FHIR_TO_UI: Record<DayOfWeekCode, ProviderAvailabilityUiDay> = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
};

function minutesToTimeLabel(totalMinutes: number): string {
  const hour = Math.floor(totalMinutes / 60) % 24;
  const minute = totalMinutes % 60;
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const period = hour < 12 ? 'AM' : 'PM';
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
}

export function weeklyHoursToUi(hours: LocationWeeklyHours): ProviderWeeklyAvailability {
  const weekly = getDefaultProviderWeeklyAvailability();
  for (const day of PROVIDER_AVAILABILITY_UI_DAYS) {
    weekly[day] = { enabled: false, slots: [] };
  }
  for (const code of DAY_OF_WEEK_CODES) {
    const window = hours[code];
    if (!window) continue;
    const uiDay = FHIR_TO_UI[code];
    weekly[uiDay] = {
      enabled: true,
      slots: [{
        start: minutesToTimeLabel(window.openingMinutes),
        end: minutesToTimeLabel(window.closingMinutes),
      }],
    };
  }
  return weekly;
}

export function uiToWeeklyHours(weekly: ProviderWeeklyAvailability): LocationWeeklyHours {
  const hours: LocationWeeklyHours = {};
  for (const uiDay of PROVIDER_AVAILABILITY_UI_DAYS) {
    const day = weekly[uiDay];
    if (!day?.enabled) continue;
    const slot = day.slots?.[0];
    if (!slot?.start || !slot.end) continue;
    const start = parseTimeLabel12h(slot.start);
    const end = parseTimeLabel12h(slot.end);
    if (!start || !end) continue;
    const openingMinutes = start.hour * 60 + start.minute;
    const closingMinutes = end.hour * 60 + end.minute;
    if (openingMinutes >= closingMinutes) continue;
    hours[UI_TO_FHIR[uiDay]] = { openingMinutes, closingMinutes };
  }
  return hours;
}

export function defaultSiteHoursUi(): ProviderWeeklyAvailability {
  const weekly = getDefaultProviderWeeklyAvailability();
  for (const day of PROVIDER_AVAILABILITY_UI_DAYS) {
    const weekday = day !== 'Sat' && day !== 'Sun';
    weekly[day] = weekday
      ? { enabled: true, slots: [{ ...DEFAULT_PROVIDER_AVAILABILITY_SLOT }] }
      : { enabled: false, slots: [] };
  }
  return weekly;
}

export function weeklyHoursToDayWindows(
  hours: LocationWeeklyHours,
): Partial<Record<ProviderAvailabilityUiDay, { openingMinutes: number; closingMinutes: number }>> {
  const out: Partial<Record<ProviderAvailabilityUiDay, { openingMinutes: number; closingMinutes: number }>> = {};
  for (const code of DAY_OF_WEEK_CODES) {
    const window = hours[code];
    if (!window) continue;
    if (!dayWindowHasSelectableRange(window.openingMinutes, window.closingMinutes)) continue;
    out[FHIR_TO_UI[code]] = {
      openingMinutes: window.openingMinutes,
      closingMinutes: window.closingMinutes,
    };
  }
  return out;
}

export function formatWeeklyHoursSummary(hours: LocationWeeklyHours): string | undefined {
  const openDays = DAY_OF_WEEK_CODES.filter((d) => hours[d]);
  if (openDays.length === 0) return undefined;
  const first = hours[openDays[0]]!;
  const sameWindow = openDays.every((d) => {
    const w = hours[d]!;
    return w.openingMinutes === first.openingMinutes && w.closingMinutes === first.closingMinutes;
  });
  const dayLabels = openDays.map((d) => FHIR_TO_UI[d]);
  const timeRange = `${minutesToTimeLabel(first.openingMinutes)}–${minutesToTimeLabel(first.closingMinutes)}`;
  if (sameWindow) {
    return `${formatDaySpan(dayLabels)} ${timeRange}`;
  }
  return openDays.map((d) => `${FHIR_TO_UI[d]} ${minutesToTimeLabel(hours[d]!.openingMinutes)}–${minutesToTimeLabel(hours[d]!.closingMinutes)}`).join(', ');
}

function formatDaySpan(days: ProviderAvailabilityUiDay[]): string {
  if (days.length === 1) return days[0];
  const ordered = PROVIDER_AVAILABILITY_UI_DAYS.filter((d) => days.includes(d));
  const consecutive =
    ordered.length === days.length
    && ordered.every((d, i) => i === 0 || PROVIDER_AVAILABILITY_UI_DAYS.indexOf(d) === PROVIDER_AVAILABILITY_UI_DAYS.indexOf(ordered[i - 1]) + 1);
  if (consecutive) return `${ordered[0]}–${ordered[ordered.length - 1]}`;
  return ordered.join(', ');
}
